import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
    Flag, Flask, Question as QuestionIcon, ChatCircleDots,
    Pulse, Users, Sparkle, ArrowRight,
} from "@phosphor-icons/react";

const POLL_MS = 25000;          // refresh the pulse every 25 seconds — fast enough to feel alive without hammering the API.

/**
 * LivePulseStrip — a horizontally-scrolling strip of today's metrics rendered
 * at the top of the home dashboard. The numbers re-poll every 25s and the
 * count animates from old → new via `useCountUp`, so it feels alive even
 * when the user is just reading.
 *
 * Two visual modes:
 *   - default     → 4 stat cards + "online now" + a server-clock badge
 *   - compact     → single inline row for mobile
 */
export default function LivePulseStrip() {
    const [pulse, setPulse] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        let alive = true;
        const fetchPulse = () => api.get("/pulse")
            .then(({ data }) => { if (alive) setPulse(data); })
            .catch(() => {});
        fetchPulse();
        const i = setInterval(fetchPulse, POLL_MS);
        return () => { alive = false; clearInterval(i); };
    }, []);

    // Independent ticking clock so the "live" feel never freezes between pulls.
    useEffect(() => {
        const i = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(i);
    }, []);

    const today = pulse?.today || { questions: 0, answers: 0, ctf_solves: 0, lab_solves: 0 };
    const online = pulse?.online_now ?? 1;
    const clock = new Date(Date.now()).toUTCString().slice(17, 25);    // "HH:MM:SS"

    const cards = [
        { id: "online", label: "online now", value: online, Icon: Users, color: "emerald" },
        { id: "questions", label: "questions today", value: today.questions, Icon: QuestionIcon, color: "cyan" },
        { id: "answers", label: "answers today", value: today.answers, Icon: ChatCircleDots, color: "violet" },
        { id: "ctf", label: "flags captured today", value: today.ctf_solves, Icon: Flag, color: "amber" },
        { id: "lab", label: "labs solved today", value: today.lab_solves, Icon: Flask, color: "rose" },
    ];

    return (
        <div
            className="relative overflow-hidden bg-gradient-to-r from-cyan-500/[0.07] via-transparent to-cyan-500/[0.07] rounded-sm"
            data-testid="live-pulse-strip"
            aria-label="Live community activity"
        >
            {/* scanline animation — purely decorative, signals "live" */}
            <div className="absolute inset-x-0 top-0 h-px bg-cyan-400/40 animate-scan" aria-hidden="true" />
            <div className="flex flex-col sm:flex-row sm:items-stretch sm:justify-between gap-2 px-3 py-2.5 sm:px-4 sm:py-3">
                {/* LIVE badge with pulse */}
                <div className="flex items-center gap-2 shrink-0 pr-3 sm:pr-4 sm:border-r sm:border-white/10">
                    <div className="relative">
                        <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-75 animate-ping" />
                        <span className="relative block w-2 h-2 rounded-full bg-emerald-400" />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-emerald-300">live</span>
                    <span className="text-[10px] font-mono text-slate-500 hidden md:inline" suppressHydrationWarning>· {clock} UTC</span>
                </div>

                <div className="flex flex-col gap-1.5 sm:flex-row sm:items-stretch sm:gap-5 sm:flex-1 min-w-0">
                    {cards.map((c) => <StatCard key={c.id} {...c} fresh={tick % 20 === 0} />)}
                </div>
            </div>
        </div>
    );
}

function useCountUp(target) {
    const [n, setN] = useState(target);
    const ref = useRef(target);
    useEffect(() => {
        const from = ref.current;
        const to = target;
        ref.current = target;
        if (from === to) return;
        const ms = 500;
        const start = performance.now();
        let raf;
        const tick = (now) => {
            const t = Math.min(1, (now - start) / ms);
            const eased = 1 - Math.pow(1 - t, 3);                       // ease-out-cubic
            const v = Math.round(from + (to - from) * eased);
            setN(v);
            if (t < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target]);
    return n;
}

function StatCard({ label, value, Icon, color, fresh }) {
    const n = useCountUp(value);
    const ring = {
        cyan: "text-cyan-300", emerald: "text-emerald-300", violet: "text-violet-300",
        amber: "text-amber-300", rose: "text-rose-300",
    }[color] || "text-cyan-300";
    return (
        <div
            className={`min-w-0 sm:flex-1 flex items-center gap-2 sm:gap-2.5 ${fresh ? "animate-pulse-once" : ""}`}
            data-testid={`pulse-stat-${label.replace(/\s+/g, "-")}`}
        >
            <Icon size={14} weight="duotone" className={`${ring} shrink-0`} />
            <div className="min-w-0 flex items-baseline gap-2 sm:block">
                <div className={`text-sm sm:text-lg font-bold leading-none ${ring}`} style={{ fontVariantNumeric: "tabular-nums" }}>
                    {n.toLocaleString()}
                </div>
                <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 leading-tight whitespace-nowrap overflow-hidden text-ellipsis sm:whitespace-normal sm:overflow-visible">{label}</div>
            </div>
        </div>
    );
}


/**
 * LiveActivityTicker — a vertically-scrolling list of recent events. Sits in
 * the right rail and gives the feed a "people are doing things right now"
 * heartbeat that pure stat counts can't convey.
 */
export function LiveActivityTicker() {
    const [pulse, setPulse] = useState(null);

    useEffect(() => {
        let alive = true;
        const f = () => api.get("/pulse")
            .then(({ data }) => { if (alive) setPulse(data); })
            .catch(() => {});
        f();
        const i = setInterval(f, POLL_MS);
        return () => { alive = false; clearInterval(i); };
    }, []);

    const items = pulse?.ticker || [];
    const meta = {
        question: { Icon: QuestionIcon, color: "text-cyan-300", verb: "asked" },
        answer: { Icon: ChatCircleDots, color: "text-violet-300", verb: "answered" },
        ctf_solve: { Icon: Flag, color: "text-amber-300", verb: "captured a flag" },
        lab_solve: { Icon: Flask, color: "text-rose-300", verb: "solved a lab" },
    };

    return (
        <section
            className="hc-surface overflow-hidden"
            data-testid="live-activity-ticker"
        >
            <header className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
                <div className="relative">
                    <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-75 animate-ping" />
                    <span className="relative block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                </div>
                <h3 className="text-[13px] font-semibold tracking-tight text-emerald-300" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>Live activity</h3>
                <Pulse size={11} weight="bold" className="text-emerald-300 ml-auto animate-pulse" />
            </header>

            <div className="divide-y divide-white/5">
                {pulse === null ? (
                    <div className="p-3 text-[11px] font-mono text-slate-500">// listening…</div>
                ) : items.length === 0 ? (
                    <div className="p-3 text-[11px] font-mono text-slate-500">// quiet on the wire — be the first today</div>
                ) : (
                    items.slice(0, 8).map((it, i) => {
                        const m = meta[it.kind] || { Icon: Sparkle, color: "text-slate-300" };
                        const Ic = m.Icon;
                        return (
                            <Link
                                key={`${it.kind}-${it.at}-${i}`}
                                to={it.target || "/"}
                                className="block px-3 py-2 hover:bg-slate-800/30 transition-colors"
                                data-testid={`ticker-row-${i}`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <Ic size={11} weight="duotone" className={`${m.color} shrink-0`} />
                                    <span className="text-[12px] text-slate-200 truncate min-w-0">
                                        <span className="text-slate-100 font-semibold">@{it.username || "anon"}</span>
                                        {" "}<span className="text-slate-400">{it.label}</span>
                                    </span>
                                    <span className="ml-auto text-[10px] font-mono text-slate-500 shrink-0">{timeAgoShort(it.at)}</span>
                                </div>
                                {it.snippet && <div className="text-[11px] text-slate-500 truncate ml-5 mt-0.5">{it.snippet}</div>}
                            </Link>
                        );
                    })
                )}
            </div>
            <Link to="/leaderboard" className="block text-center text-[10px] uppercase tracking-widest font-mono text-cyan-400 hover:text-cyan-300 py-2.5 border-t border-white/5 transition-colors">
                see who&apos;s on top <ArrowRight size={10} weight="bold" className="inline-block" />
            </Link>
        </section>
    );
}

function timeAgoShort(iso) {
    if (!iso) return "";
    const secs = (Date.now() - new Date(iso).getTime()) / 1000;
    if (secs < 60) return `${Math.max(1, Math.floor(secs))}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
    return `${Math.floor(secs / 86400)}d`;
}

// LivePulseStrip is the default export. LiveActivityTicker is named-exported above.
